import * as React from 'react';
import { useTheme } from '@mui/material/styles';
import Stack from '@mui/material/Stack';
import Button from '@mui/material/Button';
import { useTranslation } from 'react-i18next';
import { useFormik } from 'formik';
import moment from 'moment';

import { useConnection, useRecaptcha } from '@hooks';
import { greetingVar } from '@context';
import { useSignupMutation, GenderEnum, AccountTypeEnum } from 'graphql';
import initValues from './initValues';
import validationSchema from './validationSchemas';
import FormFields from './FormFields';
import Stepper from './Stepper';
import { getSteps } from './helpers';

interface SignupFormProps {
  type: 'consumer' | 'spot' | 'artist';
  handleBack: () => void;
  redirect?: string | null;
}

export default function SignupForm(props: Readonly<SignupFormProps>) {
  const { handleBack, type, redirect = null } = props;
  const [completed, setCompleted] = React.useState<{ [k: number]: boolean }>({});
  const [activeStep, setActiveStep] = React.useState(0);
  const { login } = useConnection({ redirect });
  const theme = useTheme();
  const { executeRecaptcha, isRecaptchaReady } = useRecaptcha();
  const { t } = useTranslation('field');

  const { steps, stepsCaptions, stepFieldValidations } = getSteps(type, t);

  const [signup] = useSignupMutation();

  const formik = useFormik({
    validateOnMount: true,
    initialValues: initValues(type),
    validationSchema: validationSchema(type, t),
    onSubmit: async (values) => {
      if (!isRecaptchaReady) {
        // reCAPTCHA script not ready yet
        return;
      }

      const recaptchaToken = await executeRecaptcha('signup');

      if (!recaptchaToken) {
        import.meta.env.DEV && console.error('Failed to get reCAPTCHA token');
        return;
      }

      Object.assign(values, { recaptchaToken });

      const validValues = {
        isSSO: false,
        type: type.toUpperCase() as AccountTypeEnum,
        email: values.email,
        password: values.password,
        firstname: values.firstname,
        lastname: values.lastname,
        mobilePhone: values.mobilePhone,
        gender: values.gender.toUpperCase() as GenderEnum,
        birthdate: moment(values.birthdate).toISOString(),
        siret: values.siret,
        isAcceptedTOS: values.isAcceptedTOS,
        isAcceptedPrivacyPolicy: values.isAcceptedPrivacyPolicy,
        isAcceptedNewsletter: values.isAcceptedNewsletter,
      };

      // Signup mutation
      signup({
        variables: { input: validValues },
        async onCompleted() {
          // Auto login after successful signup
          await login({
            variables: {
              input: {
                email: formik.values.email,
                password: formik.values.password,
                recaptchaToken,
              },
            },
          });

          greetingVar({
            firstname: formik.values.firstname,
            email: formik.values.email,
          });
        },
        onError(error) {
          // @ts-expect-error networkError is not a property of ServerError
          const networkError = error?.networkError?.result?.errors?.[0]?.message;

          if (networkError === 'EMAIL_ALREADY_REGISTERED') {
            formik.setErrors({ email: 'Email déjà enregistré' });
          } else if (networkError === 'SIRET_ALREADY_REGISTRED') {
            formik.setErrors({ siret: 'Établissemnt déjà enregistré' });
          }
        },
      });
    },
  });

  React.useEffect(() => {
    if (activeStep !== steps.length) {
      // avoid checking the last step (summary)
      const isStepFieldValid = stepFieldValidations[activeStep].every(
        (field) => !formik.errors[field as keyof typeof formik.errors]
      );

      setCompleted({ ...completed, [activeStep]: isStepFieldValid });
    }
  }, [activeStep, formik.values, formik.errors]);

  React.useEffect(() => {
    // Reset form on unmount
    return () => {
      formik.resetForm({ values: initValues(type) });
    };
  }, []);

  const handleNext = () => {
    if (activeStep < steps.length) {
      setActiveStep(activeStep + 1);
    }
  };

  const handleBackStep = () => {
    setActiveStep(activeStep - 1);
  };

  const handleStep = (step: number) => () => {
    setActiveStep(step);
  };

  return (
    <form
      onSubmit={formik.handleSubmit}
      style={{
        width: '100%',
        height: '100%',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
      }}
    >
      <Stack
        width="100%"
        maxWidth={{ sx: '400px', sm: '500px', md: '600px' }}
        spacing={10}
        justifyContent="center"
        alignItems="center"
      >
        {activeStep < steps.length && (
          <Stepper
            steps={steps}
            stepsCaptions={stepsCaptions}
            activeStep={activeStep}
            completed={completed}
            handleStep={handleStep}
          />
        )}
        <FormFields type={type} activeStep={activeStep} formik={formik} />
        <Stack direction="row" width="100%" justifyContent="space-between">
          <Button
            variant="contained"
            color="uncolored"
            type="button"
            disabled={activeStep === 0}
            onClick={handleBackStep}
            sx={{ display: activeStep === 0 ? 'none' : 'block' }}
          >
            {t('back', { ns: 'cta' })}
          </Button>
          {activeStep < steps.length ? (
            <Button
              variant="contained"
              type="button"
              onClick={handleNext}
              disabled={activeStep !== steps.length ? !completed[activeStep] : !formik.isValid}
              color="success"
              sx={{ ml: 'auto' }}
              startIcon={activeStep === steps.length - 1 ? <theme.icons.summary /> : null}
            >
              {activeStep === steps.length - 1 ? t('summary', { ns: 'cta' }) : t('next', { ns: 'cta' })}
            </Button>
          ) : (
            <Button
              variant="contained"
              type="button"
              onClick={() => {
                if (activeStep === steps.length) {
                  formik.handleSubmit();
                }
              }}
              disabled={
                !formik.isValid || !formik.values.isAcceptedTOS || !formik.values.isAcceptedPrivacyPolicy
              }
              color="success"
              sx={{ ml: 'auto' }}
              startIcon={<theme.icons.rocket />}
            >
              {t("let's go", { ns: 'cta' })}
            </Button>
          )}
        </Stack>
      </Stack>
    </form>
  );
}
